import React from "react";
import MDialog from "@/components/ui/atoms/MDialog";
import UncategorizedDrawer from "./UncategorizedDrawer";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IconFileTypePdf,
  IconFileTypeDoc,
  IconFileTypeJpg,
  IconFileTypePng,
  IconAlertSquareRoundedFilled,
} from "@tabler/icons-react";
import { MButton } from "@/components/ui/atoms/MButtons";
import styles from "./document.module.css";
import MHeader from "@/components/ui/organisms/MHeader";
import { useDocument } from "@/hooks/useDocument";
import { getMemberInfo } from "@/hooks/useMembers";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import MLoader from "@/components/ui/atoms/MLoader";
import { DocumentStatus } from "@/types/document.dto";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";

const PAGE_SIZE = 10;

const fileTypeIconMap: Record<string, React.ReactNode> = {
  pdf: <IconFileTypePdf size={20} className={styles.fileTypeIcon} />,
  doc: <IconFileTypeDoc size={20} className={styles.fileTypeIcon} />,
  docx: <IconFileTypeDoc size={20} className={styles.fileTypeIcon} />,
  jpeg: <IconFileTypeJpg size={20} className={styles.fileTypeIcon} />,
  jpg: <IconFileTypeJpg size={20} className={styles.fileTypeIcon} />,
  png: <IconFileTypePng size={20} className={styles.fileTypeIcon} />,
};

const getFileTypeIcon = (fileName: string) => {
  const ext = fileName?.split(".").pop()?.toLowerCase();
  return (
    fileTypeIconMap[ext || ""] || (
      <IconFileTypePdf size={20} className={styles.fileTypeIcon} />
    )
  );
};

const UncategorizedDocuments: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const autoOpenReview = (location.state as any)?.autoOpenReview === true;
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const {
    fetchUncategorizedDocuments,
    deleteDocument,
    updateUncategorizedDocument,
  } = useDocument(memberId);
  const { showToast } = useToast();

  // ✅ Pagination & data state
  const [documents, setDocuments] = React.useState<any[]>([]);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [loading, setLoading] = React.useState(true);
  const [hasMore, setHasMore] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const pageRef = React.useRef(1);

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<any>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerDoc, setDrawerDoc] = React.useState<any>(null);

  // ✅ Unified fetch for infinite scroll
  const loadDocuments = React.useCallback(
    async (reset = false) => {
      try {
        if (reset) {
          setLoading(true);
          setPage(1);
          pageRef.current = 1;
          setHasMore(true);
        }

        const currentPage = reset ? 1 : pageRef.current;

        const payload = {
          filters: {
            status: {
              value: DocumentStatus.Uncategorized,
              type: "number",
              operation: "eq",
            },
          },
          sort: [],
          pagination: { page: currentPage, pageSize: PAGE_SIZE },
          page: currentPage,
          pageSize: PAGE_SIZE,
        };

        const res = (await fetchUncategorizedDocuments(payload)) as {
          data: any[];
          total: number;
        };
        const data = Array.isArray(res?.data) ? res.data : [];
        setTotalCount(typeof res?.total === "number" ? res.total : data.length);

        if (reset) {
          setDocuments(data);
        } else {
          setDocuments((prev) => [...prev, ...data]);
        }

        setHasMore(data.length === PAGE_SIZE);

        if (data.length > 0) {
          setPage((prev) => {
            const next = prev + 1;
            pageRef.current = next;
            return next;
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [fetchUncategorizedDocuments]
  );

  // ✅ Initial load
  React.useEffect(() => {
    loadDocuments(true);
  }, [memberId]);

  // Auto-open drawer for the first document when navigated from the dashboard banner
  React.useEffect(() => {
    if (autoOpenReview && !loading && documents.length > 0 && !drawerOpen) {
      setDrawerDoc(documents[0]);
      setDrawerOpen(true);
    }
  }, [autoOpenReview, loading, documents]);

  const handleDrawerDone = async ({
    displayName,
    assignedTo,
    fileCategory,
    comment,
  }: any) => {
    setDrawerOpen(false);
    if (!drawerDoc) return;

    const payload = {
      memberId: assignedTo?.id,
      category: fileCategory,
      notes: comment,
      displayName,
      ocrReadable: true,
      status: DocumentStatus.Processed,
      ocrError: null,
      categorize: true,
    };
    await updateUncategorizedDocument(drawerDoc, payload);

    /*showToast({
      message: 'Document successfully categorized.',
      variant: 'success',
      duration: 4000,
      position: 'top'
    });*/

    // Refresh from page 1 after categorization
    await loadDocuments(true);
  };

  return (
    <div className="profile-page-root">
      <MHeader
        module="documents"
        memberInfo={memberInfo}
        title="Review needed"
        description="Choose the correct document owner or type to organise these files."
        learnMoreContent={
          <>
            Maatra reads each document to organise it by owner and type.
            <br /> <br /> If a document needs your action, it means we couldn’t
            organise it confidently and need your input. You can review your
            files from here and organise them correctly.
          </>
        }
        disableSticky
      />

      {loading && documents.length === 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <MLoader size={56} color="#4C189A" />
        </div>
      ) : documents.length === 0 ? (
        <MEmptyCard primaryTitle="You have no uncategorised documents" />
      ) : (
        <MInfiniteScroll
          data={documents}
          hasMore={hasMore}
          isLoading={loading}
          onLoadMore={() => loadDocuments(false)}
          renderItem={(doc, idx) => (
            <div
              className={styles.uncategorizedCard}
              key={doc._id || doc.id || idx}
              onClick={() =>
                navigate("/documents/uncategorized/view", {
                  state: { document: doc, backUrl: "/documents/uncategorized" },
                })
              }
              style={{ cursor: "pointer" }}
            >
              <div className={styles.uncategorizedCardLeft}>
                {getFileTypeIcon(doc.media?.fileName || doc.displayName)}
              </div>
              <div className={styles.uncategorizedCardContent}>
                <div
                  style={{ marginBottom: "8px" }}
                  className={`body-base-semibold brand-colours-indigo-indigo-700 ${styles.uncategorizedFileNameTruncate}`}
                >
                  {doc.displayName || doc.media?.fileName}
                </div>
                <div className={styles.uncategorizedOcrErrorRow}>
                  <IconAlertSquareRoundedFilled
                    className={styles.uncategorizedOcrErrorIcon}
                    size={12}
                  />
                  <span className={styles.uncategorizedOcrError}>
                    {doc.ocrError || "Could not identify document owner & type"}
                  </span>
                </div>
              </div>
              <div className={styles.uncategorizedCardActions}>
                <MButton
                  size="sm"
                  variant="icon-only-outline"
                  icon="pencil"
                  className={styles.uncategorizedActionBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDrawerDoc(doc);
                    setDrawerOpen(true);
                  }}
                  aria-label="Categorize document"
                />
                <MButton
                  size="sm"
                  variant="icon-only-outline"
                  icon="trash"
                  className={`${styles.uncategorizedActionBtn} ${styles.trashBtn}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(doc);
                    setDeleteDialogOpen(true);
                  }}
                  aria-label="Delete document"
                />
              </div>
            </div>
          )}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <MDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete record?"
        description="This record will be permanently deleted."
        primaryCtaText="Yes"
        secondaryCtaText="No"
        onPrimaryCta={async () => {
          if (!deleteTarget) return;
          setDeleteLoading(true);
          try {
            await deleteDocument(deleteTarget);
            setDocuments((prev) => {
              const updatedDocs = prev.filter(
                (d) => d._id !== deleteTarget._id
              );
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
              if (updatedDocs.length === 0) navigate("/documents");
              return updatedDocs;
            });
          } catch (err) {
            // Optionally show toast
          } finally {
            setDeleteLoading(false);
          }
        }}
        onSecondaryCta={() => setDeleteDialogOpen(false)}
        isPrimaryCtaLoading={deleteLoading}
      />

      <UncategorizedDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        document={drawerDoc}
        onDone={handleDrawerDone}
      />
    </div>
  );
};

export default UncategorizedDocuments;
